// src/chef/ChefDashboard.js
import React, { useEffect, useState, useRef } from "react";
import api from "../services/api";
import KitchenItemsGrouped from "./KitchenItemsList";

/* ───────────── COMPONENTE ───────────── */
export default function ChefDashboard() {
  /* ---------- state ---------- */
  const [items, setItems]                     = useState([]);
  const [error, setError]                     = useState("");

  const [allCategories, setAllCategories]     = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(new Set());

  const [tableFilter, setTableFilter]         = useState("");
  const wsRef                                 = useRef(null);

  /* ---------- mount ---------- */
  useEffect(() => {
    openWebSocket();
    fetchKitchenItems();
    return () => { wsRef.current?.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- websocket ---------- */
  const openWebSocket = () => {
    const ws = new WebSocket(`${process.env.REACT_APP_WS_URL}/ws`);
    ws.onopen    = () => console.log("WS Chef connesso");
    ws.onmessage = (e) => {
      if (e.data === "reload_kitchen") fetchKitchenItems();
    };
    ws.onclose   = () => console.log("WS Chef chiuso");
    wsRef.current = ws;
  };

  /* ---------- API ---------- */
  const fetchKitchenItems = async () => {
    setError("");
    try {
      const { data = [] } = await api.get("/kitchen/items");
      setItems(data);

      /* aggiorniamo set categorie */
      const updated = new Set(allCategories);
      data.forEach((it) => it.menu_item_category && updated.add(it.menu_item_category));
      setAllCategories([...updated]);
    } catch (err) {
      console.error("fetchKitchenItems:", err);
      setError("Impossibile caricare i piatti da cucinare");
    }
  };

  const handleUpdateItemStatus = async (itemId, newStatus) => {
    /* optimistic UI: cambiamo subito il local state */
    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId ? { ...it, status: newStatus } : it
      )
    );

    try {
      await api.patch(
        `/kitchen/order_items/${itemId}/status`,
        { status: newStatus },
        /* ⚠️ evita che Axios vada in catch sui 2xx / 204 */
        { validateStatus: (s) => s < 400 }
      );
      // Il WS “reload_kitchen” arriverà dal backend; in fallback:
      fetchKitchenItems();
    } catch (err) {
      /* solo vero errore di rete / 4xx‑5xx */
      console.error("update item status:", err);
      setError("Impossibile aggiornare lo stato del piatto");
      fetchKitchenItems();          // rollback visuale
    }
  };

  /* ---------- filtri ---------- */
  const toggleCategory = (cat) => {
    const set = new Set(selectedCategories);
    set.has(cat) ? set.delete(cat) : set.add(cat);
    setSelectedCategories(set);
  };

  const filteredItems = items.filter((it) => {
    if (selectedCategories.size && !selectedCategories.has(it.menu_item_category)) return false;
    if (tableFilter && !String(it.table_number ?? "").includes(tableFilter))     return false;
    return true;
  });

  /* ---------- render ---------- */
  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Dashboard Chef</h1>
      {error && <p style={styles.error}>{error}</p>}

      {/* FILTRI */}
      <div style={styles.filtersContainer}>
        <div style={styles.categoriesRow}>
          {allCategories.map((cat) => {
            const active = selectedCategories.has(cat);
            return (
              <span
                key={cat}
                style={{
                  ...styles.categoryBadge,
                  backgroundColor: active ? "#FF6600" : "#ccc",
                  color:           active ? "#fff"    : "#333",
                }}
                onClick={() => toggleCategory(cat)}
              >
                {cat}
              </span>
            );
          })}
        </div>

        <input
          style={styles.tableFilterInput}
          placeholder="Filtra per tavolo..."
          value={tableFilter}
          onChange={(e) => setTableFilter(e.target.value)}
        />
      </div>

      {/* LISTA RAGGRUPPATA */}
      <KitchenItemsGrouped
        items={filteredItems}
        onUpdateItemStatus={handleUpdateItemStatus}
      />
    </div>
  );
}

/* ───────────── STILI ───────────── */
const styles = {
  container: {
    padding: 20,
    background: "#FAFAFA",
    minHeight: "100vh",
    fontSize: 16,
  },
  title:   { fontSize: 24, marginBottom: 20, color: "#333" },
  error:   { color: "red", marginBottom: 10 },
  filtersContainer: { marginBottom: 20 },
  categoriesRow:    { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 15 },
  categoryBadge: {
    padding: "8px 12px",
    borderRadius: 20,
    cursor: "pointer",
    fontWeight: "bold",
    userSelect: "none",
    fontSize: 14,
  },
  tableFilterInput: {
    width: "100%",
    padding: 10,
    fontSize: 16,
    borderRadius: 6,
    border: "1px solid #ccc",
  },
};
